import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { count } from '../../i18n/format';
import { percent } from './buckets';

export interface RankedBar {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /**
   * `alert` draws the bar in the second hue: the rows the reader should look at twice (a no-show,
   * a patient not seen for a year). The row's own label says why, so the colour never carries the
   * meaning alone.
   */
  readonly tone?: 'alert';
}

/**
 * One measure per row, as horizontal bars (CR-33).
 *
 * The reports hub's bars (`.bars`, see ReportChart) with three additions the screens' charts need:
 * the rows keep the order they are given — a status chart reads in lifecycle order and a "last
 * visit" chart in time order, and sorting either by size would scramble the question; each value
 * carries its share of the whole, because "12" says little until it is "12 · 9%"; and a row can be
 * a button that filters the screen underneath, so a chart is a way into the list rather than a
 * picture beside it.
 *
 * Bars are scaled to the largest row, not to the total, so a dominant row does not flatten the
 * rest into slivers. `format` writes the figure (money on the Sales charts, CR-34); `firstRows`
 * shows that many and puts the rest behind "Show all", never dropping them.
 */
export function RankedBars({
  rows,
  selected,
  onSelect,
  selectLabel,
  format = count,
  firstRows,
  share = true,
}: {
  readonly rows: readonly RankedBar[];
  readonly selected?: string;
  readonly onSelect?: (key: string) => void;
  /** The accessible name of a row's button, e.g. "Show only No-show". */
  readonly selectLabel?: (row: RankedBar) => string;
  readonly format?: (value: number) => string;
  readonly firstRows?: number;
  /** Each row's share of the total; off where the rows are not parts of one whole. */
  readonly share?: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const largest = rows.reduce((running, row) => Math.max(running, row.value), 0);
  const total = rows.reduce((running, row) => running + row.value, 0);
  const collapsible = firstRows !== undefined && rows.length > firstRows;
  const shown = collapsible && !expanded ? rows.slice(0, firstRows) : rows;

  return (
    <>
      <ol className="bars bars--plain">
        {shown.map((row) => {
          const content = (
            <>
              <span className="bars__label" title={row.label}>
                {row.label}
              </span>
              <span className="bars__track">
                <span
                  className={row.tone === 'alert' ? 'bars__bar bars__bar--alert' : 'bars__bar'}
                  style={{ inlineSize: `${largest > 0 ? (row.value / largest) * 100 : 0}%` }}
                />
              </span>
              <span className="bars__value numeric">
                {format(row.value)}
                {share ? <span className="bars__share"> · {percent(row.value, total)}%</span> : null}
              </span>
            </>
          );
          return (
            <li key={row.key}>
              {onSelect ? (
                <button
                  type="button"
                  className="bars__row bars__row--button"
                  aria-pressed={selected === row.key}
                  aria-label={selectLabel ? selectLabel(row) : undefined}
                  onClick={() => onSelect(row.key)}
                >
                  {content}
                </button>
              ) : (
                <div className="bars__row">{content}</div>
              )}
            </li>
          );
        })}
      </ol>
      {collapsible ? (
        <button
          type="button"
          className="button button--ghost button--compact"
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? t('overview.showFewer') : t('overview.showAll', { count: rows.length })}
        </button>
      ) : null}
    </>
  );
}
